import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import FinalCTA from "@/components/landing/FinalCTA";
import FAQ from "@/components/shared/FAQ";
import MedCard from "@/components/shared/MedCard";
import { HUBS } from "@/data/categories";
import SEO, { breadcrumbSchema } from "@/components/shared/SEO";

const ACCENT = { pink: "var(--leen-pink)", blue: "var(--leen-blue)", yellow: "var(--leen-yellow)" };

export default function CategoryHub() {
  const { slug } = useParams();
  const hub = HUBS[slug];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!hub) return (
    <main className="bg-[var(--leen-bg)] min-h-screen text-white">
      <SEO title="Not Found" path={`/category/${slug}`} noindex />
      <Nav />
      <div className="pt-40 text-center px-6">
        <h1 className="font-display text-5xl">Category not found</h1>
        <Link to="/" className="btn-pink mt-8 inline-flex">Back home →</Link>
      </div>
      <Footer />
    </main>
  );

  const color = ACCENT[hub.accent] || ACCENT.pink;
  const btn = hub.accent === "blue" ? "btn-blue" : "btn-pink";
  const path = `/category/${slug}`;

  return (
    <main className="bg-[var(--leen-bg)] text-white overflow-x-hidden min-h-screen">
      <SEO
        title={`${hub.name} · ${hub.eyebrow || "Programs"}`}
        path={path}
        description={hub.tagline || `${hub.name} programs at BestRx — provider-guided, clinician-reviewed, transparently priced.`}
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: hub.name, path },
        ])}
      />
      <Nav />
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute -top-20 -left-40 w-[500px] h-[500px] rounded-full blur-3xl opacity-30" style={{ background: color }} />
        <div className="relative px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <p className="eyebrow mb-5" style={{ color }}>{hub.eyebrow}</p>
          <h1 className="font-display text-[60px] sm:text-[100px] md:text-[140px] lg:text-[180px] leading-[0.85] tracking-tight">
            {hub.name}
          </h1>
          <p className="mt-7 font-body italic text-[18px] md:text-[22px] text-[var(--leen-ink-soft)] max-w-3xl leading-relaxed">
            {hub.tagline}
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <a href="#start-assessment" className={btn} data-testid={`hub-${slug}-cta`}>Start Your Assessment <span aria-hidden>→</span></a>
            <a href="#meds" className="btn-ghost">View Medications</a>
          </div>
        </div>
      </section>

      <section className="bg-[var(--leen-card)] py-20 md:py-28 border-y border-[var(--leen-line)]">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1100px] mx-auto">
          <p className="font-script italic" style={{ color, fontSize: "clamp(42px, 5vw, 64px)", lineHeight: 1 }}>About</p>
          <h2 className="font-display text-[36px] sm:text-[52px] md:text-[64px] leading-[0.95] mb-7 -mt-2">{hub.name}</h2>
          <p className="font-body text-[17px] md:text-[19px] text-[var(--leen-ink-soft)] leading-relaxed max-w-3xl">{hub.intro}</p>
        </div>
      </section>

      <section id="meds" className="py-20 md:py-28">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <div className="text-center mb-12">
            <p className="font-script italic -mb-2" style={{ color, fontSize: "clamp(42px, 5vw, 60px)", lineHeight: 1 }}>The</p>
            <h2 className="font-display text-[36px] sm:text-[52px] md:text-[64px] leading-none text-white">PROTOCOLS</h2>
          </div>
          {hub.meds.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {hub.meds.map((s) => <MedCard key={s} slug={s} />)}
            </div>
          ) : (
            <p className="text-center text-[var(--leen-ink-soft)] italic">Coming soon. Take the assessment to be matched to the right protocol.</p>
          )}
        </div>
      </section>

      <section className="bg-[var(--leen-card)] py-20 md:py-28 border-y border-[var(--leen-line)]">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <div className="mb-12"><p className="font-script italic -mb-2" style={{ color, fontSize: "clamp(42px, 5vw, 60px)", lineHeight: 1 }}>The</p>
            <h2 className="font-display text-[36px] sm:text-[52px] md:text-[64px] leading-none">BENEFITS</h2></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {hub.benefits.map((b, i) => (
              <div key={i} className="bg-[var(--leen-bg)] border border-[var(--leen-line)] rounded-xl p-7">
                <div className="font-display text-[40px] mb-4" style={{ color }}>{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-[18px] text-white mb-3">{b.title}</h3>
                <p className="text-[14px] text-[var(--leen-ink-soft)] leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {hub.related?.length > 0 && (
        <section className="py-16 md:py-20">
          <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
            <p className="eyebrow text-[var(--leen-ink-soft)] mb-5">Also explore</p>
            <div className="flex flex-wrap gap-3">
              {hub.related.map((r) => (
                <Link key={r} to={`/category/${r}`} className="btn-ghost" data-testid={`related-${r}`}>{HUBS[r]?.name}</Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <FAQ items={hub.faq} accent={hub.accent === "blue" ? "blue" : "pink"} title={`${hub.name} · FAQ`} />
      <FinalCTA />
      <Footer />
    </main>
  );
}
